import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Autocomplete,
  useMediaQuery,
  useTheme,
  Divider,
  Drawer,
  Badge,
  Stack,
} from "@mui/material";
import moment from "moment";
import { useTranslation } from "react-i18next";
import MomentUtils from "@date-io/moment";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { Clear, Search, SearchOutlined, FilterList } from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DownloadExcelDialog } from "src/_modules/common";
import {
  useLazyGetWalletTransactionsXlsQuery,
  useLazyGetWalletTransactionsXlsByAdminQuery,
} from "src/_api/mapitShipApi";
import { isEmpty, omit, omitBy } from "lodash";
import { useSelector } from "react-redux";
import { ROLES } from "src/constants";
import { MerchantAutocomplete } from "src/_modules/common/ServerAutocomplete";
import { LoadingButton } from "@mui/lab";

export default function TransactionsFilter({
  params,
  filterParams: filterParamsProp,
  setParams,
  handleChange,
  resetFilters,
  rowCount,
  disableExcelSend = false,
  onSubmit,
  isLoading,
  excel = true,
  ...props
}) {
  const { t } = useTranslation();
  const { profile } = useSelector((state) => state.entities.profile);
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));

  // Use filterParams if provided (useParams hook mode), otherwise fall back to params (legacy mode)
  const filterParams = filterParamsProp ?? params;

  const [openExcelDialog, setOpenExcelDialog] = useState(false);
  const [error, setError] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const [
    getWalletTransactionsXls,
    {
      isLoading: getTrasactionsByMerchantLoading,
      isFetching: isFetchingByMerchant,
    },
  ] = useLazyGetWalletTransactionsXlsQuery();
  const [
    getWalletTransactionsXlsByAdmin,
    { isLoading: getTrasactionsByAdminLoading, isFetching: isFetchingByAdmin },
  ] = useLazyGetWalletTransactionsXlsByAdminQuery();

  const isXlsLoading =
    getTrasactionsByMerchantLoading ||
    getTrasactionsByAdminLoading ||
    isFetchingByMerchant ||
    isFetchingByAdmin;

  const onSendHandler = async ({ email }) => {
    try {
      if (profile?.role === ROLES.admin) {
        await getWalletTransactionsXlsByAdmin({
          ...omitBy(omit(params, ["page", "limit"]), (v) => !v),
          email,
        }).unwrap();
      } else {
        await getWalletTransactionsXls({
          ...omitBy(omit(params, ["page", "limit"]), (v) => !v),
          email,
        }).unwrap();
      }
      setIsSuccess(true);
    } catch (error) {
      setError(error);
    }
  };

  useEffect(() => {
    if (!openExcelDialog) {
      setIsSuccess(false);
      setError(null);
    }
  }, [openExcelDialog]);

  // Drawer state
  const [open, setOpen] = useState(false);
  const [snapshot, setSnapshot] = useState({});

  const handleOpenDrawer = useCallback(() => {
    setSnapshot(filterParams);
    setOpen(true);
  }, [filterParams]);

  const handleCloseDrawer = useCallback(() => {
    setParams(snapshot);
    setOpen(false);
  }, [setParams, snapshot]);

  const handleSubmitAndClose = useCallback(() => {
    onSubmit();
    setOpen(false);
  }, [onSubmit]);

  // Reset all filter values (keep page, limit, status) and submit
  const handleResetFilters = useCallback(() => {
    const resetParams = { page: 1, limit: params?.limit, status: params?.status };
    setParams(resetParams);
    setOpen(false);
    // Defer onSubmit so state and ref are updated before applyFilters reads them
    setTimeout(() => {
      onSubmit();
    }, 0);
  }, [params?.limit, params?.status, setParams, onSubmit]);

  const hasActiveFilters = useMemo(() => {
    return Object.values(omit(filterParams, ["page", "limit", "status"])).some(
      (value) => !isEmpty(value),
    );
  }, [filterParams]);

  const categoriesOptions = useMemo(() => {

    const categories = [
      "WALLET_CHARGE",
      "WALLET_BONUS_CHARGE",
      "REFUND_COD_FEES",
      "REFUND_DELIVERY_FEES",
      "ADDITIONAL_CHARGE",
      "DEDUCT_DELIVERY_FEES",
      "WALLET_BONUS_DEDUCT",
      "WALLET_COD_CHARGE", ,
      "PLAN_SUBSCRIPTION", ,
      "TASK_COST", ,
      "SMS_LOCATION_COST", ,
      "SMS_PROOF_OF_DELIVERY_COST",
      "SMS_TRACKING_COST",
      "REFERRAL_COMMISSION",
      "NATIONAL_ADDRESS_COST",
      "CARD_ON_DELIVERY_DEDUCTION",
      "RAMADAN_OFFER",
      "WALLET_CASHBACK"
    ]

    if (profile?.role === ROLES.admin || !isEmpty(profile?.merchant?.shopify)) {
      categories.push("SHOPIFY_WALLET_CHARGE")
    }

    return categories
  }, [profile])

  const drawerWidth = isMdDown ? "100%" : 400;

  const renderFilterContent = () => (
    <Stack spacing={2}>
      <TextField
        fullWidth
        name="searchKey"
        value={filterParams?.searchKey}
        onChange={handleChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
        placeholder={t("Search")}
        variant="outlined"
        size="small"
      />
      <TextField
        fullWidth
        name="order"
        value={filterParams?.order}
        onChange={handleChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
        placeholder={t("Order No.")}
        variant="outlined"
        size="small"
      />
      <TextField
        fullWidth
        name="shipmentNumber"
        value={filterParams?.shipmentNumber ?? ""}
        onChange={handleChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
        placeholder={t("Shipment Number")}
        variant="outlined"
        size="small"
      />
      <TextField
        fullWidth
        name="merchantOrderNumber"
        value={filterParams?.merchantOrderNumber ?? ""}
        onChange={handleChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
        placeholder={t("Order reference number")}
        variant="outlined"
        size="small"
      />
      <FormControl size="small" fullWidth>
        <InputLabel id="status-label">{t("Transaction type")}</InputLabel>
        <Select
          name="type"
          labelId="type-label"
          id="type"
          value={filterParams?.type ?? ""}
          label={t("Transaction type")}
          onChange={handleChange}
          startAdornment={
            !!filterParams?.type ? (
              <InputAdornment position="start">
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    handleChange({ target: { name: "type", value: null } });
                  }}
                  size="small"
                  color="primary"
                >
                  <Clear sx={{ fontSize: "inherit" }} />
                </IconButton>
              </InputAdornment>
            ) : (
              ""
            )
          }
        >
          <MenuItem value="CREDIT">{t(`transactionType.CREDIT`)}</MenuItem>
          <MenuItem value="DEBIT">{t(`transactionType.DEBIT`)}</MenuItem>
        </Select>
      </FormControl>
      <Box display="flex" gap={1}>
        <LocalizationProvider dateAdapter={MomentUtils}>
          <DatePicker
            inputFormat="DD/MM/YYYY"
            error={true}
            maxDate={
              filterParams?.toDate ? moment(filterParams?.toDate) : moment()
            }
            label={t("From")}
            value={
              filterParams?.fromDate ? moment(filterParams?.fromDate) : null
            }
            onChange={(newValue) => {
              handleChange({
                target: {
                  name: "fromDate",
                  value: newValue
                    ? moment(newValue).startOf("day").toISOString()
                    : null,
                },
              });
            }}
            renderInput={(InputParams) => {
              InputParams.InputProps = {
                ...InputParams.InputProps,
                startAdornment: !!filterParams?.fromDate ? (
                  <InputAdornment position="start">
                    <IconButton
                      onClick={(event) => {
                        event.stopPropagation();
                        handleChange({
                          target: { name: "fromDate", value: null },
                        });
                      }}
                      size="small"
                      color="primary"
                    >
                      <Clear sx={{ fontSize: "inherit" }} />
                    </IconButton>
                  </InputAdornment>
                ) : (
                  ""
                ),
              };
              return <TextField fullWidth size="small" {...InputParams} />;
            }}
          />
          <DatePicker
            inputFormat="DD/MM/YYYY"
            maxDate={moment()}
            minDate={
              filterParams?.fromDate
                ? moment(filterParams?.fromDate)
                : moment().subtract(1, "y")
            }
            label={t("To")}
            value={filterParams?.toDate ? moment(filterParams?.toDate) : null}
            onChange={(newValue) => {
              handleChange({
                target: {
                  name: "toDate",
                  value: newValue
                    ? moment(newValue).endOf("day").toISOString()
                    : null,
                },
              });
            }}
            renderInput={(InputParams) => {
              InputParams.InputProps = {
                ...InputParams.InputProps,
                startAdornment: !!filterParams?.toDate ? (
                  <InputAdornment position="start">
                    <IconButton
                      onClick={(event) => {
                        event.stopPropagation();
                        handleChange({
                          target: { name: "toDate", value: null },
                        });
                      }}
                      size="small"
                      color="primary"
                    >
                      <Clear sx={{ fontSize: "inherit" }} />
                    </IconButton>
                  </InputAdornment>
                ) : (
                  ""
                ),
              };
              return <TextField fullWidth size="small" {...InputParams} />;
            }}
          />
        </LocalizationProvider>
      </Box>
      <Autocomplete
        multiple
        options={categoriesOptions}
        value={filterParams?.categories ?? []}
        getOptionLabel={(option) => t(`transactionsCategories.${option}`)}
        onChange={(event, newValue) => {
          handleChange({ target: { name: "categories", value: newValue } });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label={t("Categories")}
            placeholder={t("Categories")}
            size="small"
          />
        )}
      />
      <Autocomplete
        multiple
        options={["PAID", "CANCELED", "PENDINGCANCELED", "REVOKED"]}
        value={filterParams?.currentStatus ?? []}
        getOptionLabel={(option) => t(`transactionStatus.${option}`)}
        onChange={(event, newValue) => {
          handleChange({
            target: { name: "currentStatus", value: newValue },
          });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label={t("Status")}
            placeholder={t("Status")}
            size="small"
          />
        )}
      />
      {profile?.role === ROLES.admin && (
        <>
          <MerchantAutocomplete
            name="merchant"
            value={filterParams?.merchant}
            onChange={handleChange}
          />
          <FormControl size="small" fullWidth>
            <InputLabel id="source-label">{t("Payment Source")}</InputLabel>
            <Select
              name="source"
              labelId="source-label"
              id="source"
              value={filterParams?.source}
              label={t("Payment Source")}
              onChange={handleChange}
              startAdornment={
                !!filterParams?.source ? (
                  <InputAdornment position="start">
                    <IconButton
                      onClick={(event) => {
                        event.stopPropagation();
                        handleChange({
                          target: { name: "source", value: null },
                        });
                      }}
                      size="small"
                      color="primary"
                    >
                      <Clear sx={{ fontSize: "inherit" }} />
                    </IconButton>
                  </InputAdornment>
                ) : (
                  ""
                )
              }
            >
              <MenuItem value="MERCHANT">{t(`Merchant`)}</MenuItem>
              <MenuItem value="ADMIN">{t(`Admin`)}</MenuItem>
            </Select>
          </FormControl>
          {profile?.role === ROLES.admin && (
            <TextField
              fullWidth
              select
              name="integrationType"
              label={t("Integration Type")}
              value={filterParams?.integrationType ?? ""}
              onChange={handleChange}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: !!filterParams?.integrationType ? (
                  <InputAdornment position="start">
                    <IconButton
                      onClick={(event) => {
                        event.stopPropagation();
                        handleChange({
                          target: { name: "integrationType", value: null },
                        });
                      }}
                      size="small"
                      color="primary"
                    >
                      <Clear sx={{ fontSize: "inherit" }} />
                    </IconButton>
                  </InputAdornment>
                ) : (
                  ""
                ),
              }}
            >
              <MenuItem value="aramex">{t("Aramex")}</MenuItem>
              <MenuItem value="oto">{t("OTO")}</MenuItem>
              <MenuItem value="secom">{t("SMSA")}</MenuItem>
              <MenuItem value="Redbox">{t("RedBox")}</MenuItem>
            </TextField>
          )}
        </>
      )}
    </Stack>
  );

  return (
    <Box {...props}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h3">{t("Transactions")}</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Badge
            invisible={!hasActiveFilters}
            color="error"
            badgeContent={
              <Clear
                sx={{ fontSize: 16, fontWeight: "bold", cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleResetFilters();
                }}
              />
            }
            sx={{
              "& .MuiBadge-badge": {
                width: 20,
                height: 20,
                minWidth: 20,
                p: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
            }}
          >
            <Button
              onClick={handleOpenDrawer}
              color="primary"
              variant="outlined"
              startIcon={<FilterList />}
              sx={{ textTransform: "none" }}
            >
              {t("Filter")}
            </Button>
          </Badge>
          {excel &&
            <Button
              color="success"
              sx={{ textTransform: "none" }}
              variant="contained"
              onClick={() => setOpenExcelDialog(true)}
            >
              {t("Excel")
              }
            </Button>}
        </Box>
        <DownloadExcelDialog
          open={openExcelDialog}
          onSend={onSendHandler}
          limit={rowCount}
          onClose={() => setOpenExcelDialog(false)}
          loading={isXlsLoading}
          isSuccess={isSuccess}
          error={error}
          disabledSend={disableExcelSend}
          options={["EMAIL"]}
        />
      </Box>

      <Drawer
        anchor="right"
        open={open}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: drawerWidth,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            pt: 10,
          }}
        >
          <Typography variant="h6">{t("Filter")}</Typography>
          <IconButton onClick={handleCloseDrawer} size="small" color="primary">
            <Clear />
          </IconButton>
        </Box>
        <Divider />

        {/* Body */}
        <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 2 }}>
          {renderFilterContent()}
        </Box>

        {/* Footer */}
        <Divider />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            gap: 1,
          }}
        >
          <Button
            onClick={handleResetFilters}
            variant="outlined"
            color="primary"
            sx={{ textTransform: "none", flex: 1 }}
          >
            {t("Reset")}
          </Button>
          <LoadingButton
            onClick={handleSubmitAndClose}
            variant="contained"
            color="primary"
            startIcon={<SearchOutlined />}
            sx={{ textTransform: "none", flex: 1 }}
            loading={isLoading}
          >
            {t("Search")}
          </LoadingButton>
        </Box>
      </Drawer>
    </Box>
  );
}
